import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface EntityNameResponse {
  name?: string;
  title?: string;
  fullName?: string;
  // CMS content detail (GET /admin/content/{id}) nests the name under `node`.
  node?: { name?: string };
}

/**
 * Maps a parent route segment to its API endpoint and a selector that extracts
 * the display name from the response.
 */
const ENTITY_CONFIG: Record<string, { endpoint: string; select: (data: EntityNameResponse) => string | undefined }> = {
  organizations: { endpoint: '/admin/organizations', select: (d) => d.name },
  library: { endpoint: '/admin/library', select: (d) => d.title },
  'curated-playlists': { endpoint: '/admin/curated-playlists', select: (d) => d.name },
  'admin-users': { endpoint: '/admin/admin-users', select: (d) => d.fullName },
  content: { endpoint: '/admin/content', select: (d) => d.node?.name },
};

/**
 * Returns a human-readable breadcrumb label for a URL segment.
 *
 * If the segment is a UUID and the preceding segment identifies a known entity
 * type, the hook fetches the entity name from the API. Otherwise it falls back
 * to a capitalised/hyphen-replaced version of the raw segment.
 */
export function useBreadcrumbLabel(
  segment: string,
  parentSegment: string | undefined,
): { label: string; isLoading: boolean } {
  const isUuid = UUID_REGEX.test(segment);
  const config = parentSegment ? ENTITY_CONFIG[parentSegment] : undefined;
  const shouldFetch = isUuid && !!config;

  const { data, isLoading } = useQuery({
    queryKey: ['breadcrumb', parentSegment, segment],
    queryFn: () => apiClient.get<EntityNameResponse>(`${config!.endpoint}/${segment}`),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000, // entity names rarely change
  });

  if (!isUuid) {
    const raw = decodeURIComponent(segment).replace(/-/g, ' ');
    const label = raw.replace(/\b\w/g, (c) => c.toUpperCase());
    return { label, isLoading: false };
  }

  if (!config) {
    return { label: segment, isLoading: false };
  }

  const name = data ? config.select(data) : undefined;
  return { label: name ?? '…', isLoading };
}
